/**
 * web/src/components/layout/EventSelector.tsx
 * Event selector card in the sidebar. Click opens a Radix DropdownMenu
 * listing the ACTIVE events from /api/events (plus the current selection
 * even if archived, so the selection stays visible); clicking one calls
 * `useEvent.setEventId(id)` which (a) persists `pixel:active-event-id`
 * to localStorage and (b) updates the React Query cache key for any
 * scoped queries (e.g., useWorkflows reads `eventId` from useEvent).
 *
 * Design contract: planning-docs/design_handoff_pixel_dashboard/design_files/
 * shell.jsx lines 62-84 + styles.css `.event-sel` (lines 158-204).
 *
 * Pages MUST NOT bypass this component when changing events; the
 * sidebar is the single switching surface so subsequent navigations
 * inherit the new active event.
 */
import { useEvent } from '../../hooks/useEvent';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';

/**
 * Renders the active-event card + dropdown. Loads the event list lazily
 * via `useEvent` (which proxies useEvents) so the sidebar isn't gated on
 * the events fetch — the trigger renders a skeleton placeholder until
 * data lands.
 *
 * @sideEffects opens a Radix dropdown on click; setEventId writes to
 *              localStorage and updates React Query cache scope
 * @idempotent yes — picking the same event twice is a no-op
 */
export function EventSelector() {
  const { event, events, setEventId, isPending } = useEvent();

  // Only offer active events as switch targets. The currently-selected event
  // stays listed even if archived so the checkmark/selection isn't invisible —
  // you can see what you're on and switch off it, just never back onto it.
  const selectable = events?.filter((e) => e.isActive || e.id === event?.id);

  if (isPending) {
    return (
      <div
        className="mx-3 my-3 px-3 py-2.5 rounded-lg bg-side-bg-elev border border-side-border animate-pulse motion-reduce:animate-none"
        aria-busy="true"
      >
        <div className="h-4 w-24 bg-side-hover rounded" />
      </div>
    );
  }

  const code = event?.code ?? '???';
  const eventName = event?.name ?? 'No event selected';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'mx-3 my-3 px-3 py-2.5 rounded-lg flex items-center gap-2.5 w-[calc(100%-1.5rem)]',
          'bg-side-bg-elev border border-side-border text-side-text',
          'hover:bg-side-hover transition-colors duration-100 cursor-pointer outline-none',
          'focus-visible:ring-2 focus-visible:ring-accent',
        )}
        aria-label="Switch active event"
      >
        <span
          className="w-7 h-7 grid place-items-center rounded-md text-[11px] font-semibold text-white -tracking-[0.02em] shrink-0"
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
          }}
        >
          {code.slice(0, 3)}
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block text-[10px] uppercase tracking-wider font-medium text-side-text-faint mb-px">
            Active Event
          </span>
          <span className="block text-[13px] font-semibold truncate -tracking-[0.01em]">
            {code} — {eventName}
          </span>
        </span>
        <Icon name="chevron-down" size={14} className="text-side-text-muted shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="min-w-[224px] bg-side-bg-elev border-side-border text-side-text"
      >
        {selectable?.map((e) => (
          <DropdownMenuItem
            key={e.id}
            onSelect={() => setEventId(e.id)}
            className="hover:bg-side-hover data-[highlighted]:bg-side-hover data-[highlighted]:text-side-text text-white"
          >
            <span className="flex-1 truncate">
              {e.code} — {e.name}
            </span>
            {e.id === event?.id && <Icon name="check" size={14} className="text-accent shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
